import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Typography } from '@mui/material';

import MuiIcon from '../../../../../../../../shared/styles/mui/helpers/mui-icon.helper';
import IReportFilterCriteria from '../../../../../../../../shared/contracts/IReportFilterCriteria';
import ReportDataFiltersDialogService from '../../report-data-filters-dialog.service';
import { AdvancedFilterOptionsContext } from '../../../../../../../../shared/context/advanced-filter-options/advanced-filter-options-context';


interface IProps {
  filterIndex: number;
  filterCriteria: IReportFilterCriteria;
  onEditClick: (filterIndex: number, filterCriteria: IReportFilterCriteria) => void;
  onDeleteClick: (filterIndex: number, filterCriteria: IReportFilterCriteria) => void;
  onFilterValueChange: (filterIndex: number, filterValue: string | null) => void,
}
//TODO : need to reuse the conditional-range-text-fields-component as a lot of code,
//Facing issues in getting the value from Update screen to sync with the filter field chip
function FilterFieldComponent({ filterIndex, filterCriteria, onEditClick, onDeleteClick, onFilterValueChange }: IProps) {

  const [showEdit, setShowEdit] = useState<boolean>(false);
  const showRangeTextFields = ReportDataFiltersDialogService.isRangeFilterOperatorValue(filterCriteria.operator);
  const { advancedFilterOptions } = useContext(AdvancedFilterOptionsContext)!;
  const { filterOperatorValues } = advancedFilterOptions;

  const getFilterLabel = (filterCriteria: IReportFilterCriteria, isSecondLabel?: boolean) => {
    if (ReportDataFiltersDialogService.isRangeFilterOperatorValue(filterCriteria.operator)) {
      return isSecondLabel ? "And" : filterCriteria.operatorName ?? "Value";
    }

    return ReportDataFiltersDialogService.isFilterOperatorBlank(filterCriteria.operator)
      ? 'IS'
      : filterCriteria.operatorName ?? "Value";
  };

  const getFilterDisplayValue = (filterCriteria: IReportFilterCriteria, filterValue: string) => {
    return (ReportDataFiltersDialogService.isFilterOperatorBlank(filterCriteria.operator) ?
      filterCriteria.operatorName : filterValue) ?? "";
  };

  const parseFilterValue = (filterValue: string | null) => {
    const [value1, value2] = (filterValue || "").split(",");
    return { value1: value1 || "", value2: value2 || "" };
  };
  const [fieldValues, setFieldValues] = useState(() => parseFilterValue(filterCriteria.value ?? ""));

  useEffect(() => {
    setFieldValues(parseFilterValue(filterCriteria.value ?? ""));
  }, [filterCriteria.value]);

  const handleInputChange = (field: "value1" | "value2") => (e: React.ChangeEvent<HTMLInputElement>) => {
    const targetValue = e.target.value.trim();

    setFieldValues((prevValues) => {
      const updatedValues = { ...prevValues, [field]: targetValue };

      // Combine only visible field values
      const combinedValue = [
        updatedValues.value1,
        showRangeTextFields ? updatedValues.value2 : null // Include value2 only if it's visible
      ]
        .filter((val) => val) // Remove empty or null values
        .join(",");

      onFilterValueChange(filterIndex, combinedValue || null)
      return updatedValues;
    });
  };

  const handleFilterValueDdlChange = (e: SelectChangeEvent<string>) => {
    const menuItemIndex = e.target.value;
    if (menuItemIndex) {
      onFilterValueChange(filterIndex, filterOperatorValues.find(op => op.key == menuItemIndex)?.key ?? null);
    } else {
      onFilterValueChange(filterIndex, null);
    }
  };

  return (
    <Grid container direction='column' justifyItems='stretch' justifyContent='stretch'
      onMouseEnter={() => setShowEdit(true)} onMouseLeave={() => setShowEdit(false)}
      sx={{ px: 1, pt: 0, pb: 1, borderRadius: '12px', backgroundColor: (t) => t.palette.background.default }}
    >

      {/* Filter-Field */}
      <Grid item container direction='row' wrap='nowrap' sx={{ px: 1, py: 1, height: '42px' }}>

        <Grid item xs={showEdit ? 6 : 12} >
          <Typography whiteSpace='nowrap' variant='subtitle2'>{`${filterCriteria.description}`}</Typography>
        </Grid>

        {/* Apply/Cancel */}
        {showEdit && <Grid xs={6} item container wrap='nowrap' direction='row' justifyContent='end'
          sx={{ px: 1, borderRadius: '8px', backgroundColor: (t) => t.palette.background.default }}>

          <Button size='small' color='error' variant='text' sx={{ mr: 1, borderRadius: '8px', textTransform: 'lowercase' }}
            onClick={() => onDeleteClick(filterIndex, filterCriteria)} startIcon={MuiIcon.REMOVE_TRASH()}>Delete</Button>

          <Button size='small' color='warning' variant='text' sx={{ mr: 0, borderRadius: '8px', textTransform: 'lowercase' }}
            onClick={() => onEditClick(filterIndex, filterCriteria)} startIcon={MuiIcon.EDIT()}>Edit</Button>

        </Grid>}

      </Grid>

      {/* Filter-value */}
      <Grid item sx={{ pr: 2 }}>
        <FormControl margin='none' fullWidth size='small' required sx={{ m: 1, minWidth: 120 }}>
          {/* <ConditionalRangeTextFields
            showRangeTextFields={ReportDetailFiltersDialogService.isRangeFilterOperatorValue(filterCriteria.operator)}
            filterValue={getFilterDisplayValue(filterCriteria)}
            //filterValue={filterCriteria.value ?? ""}
            onValueChange={(newValue) => onFilterValueChange(filterIndex, newValue)} // Update directly
            //setFilterValue={setValue}
            labels={{
              label1: getFilterLabel(filterCriteria, false), // "between"
              label2: getFilterLabel(filterCriteria, true), // "AND"
            }}
          /> */}
          {!ReportDataFiltersDialogService.isOperatorValueFieldDdl(filterCriteria.operatorValueId) &&
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                size="small"
                label={getFilterLabel(filterCriteria, false)}
                value={getFilterDisplayValue(filterCriteria, fieldValues.value1)}
                onChange={handleInputChange("value1")}
              />
              {showRangeTextFields && (
                <TextField
                  size="small"
                  label={getFilterLabel(filterCriteria, true)}
                  value={getFilterDisplayValue(filterCriteria, fieldValues.value2)}
                  onChange={handleInputChange("value2")}
                />
              )}
            </Box>}

          {/* For filter column types having dropdown values */}
          {ReportDataFiltersDialogService.isOperatorValueFieldDdl(filterCriteria.operatorValueId) &&
            <>
              <InputLabel shrink={true}>Value</InputLabel>
              <Select
                // open={openFilterValue}
                //onOpen={() => setOpenFilterValue(true)}
                //onClose={() => setOpenFilterValue(false)}
                value={filterCriteria.value ?? ""}
                renderValue={(value) => {
                  const displayValue = filterOperatorValues.find((x) => x.key === filterCriteria.value)?.value;
                  return displayValue || '';
                }}
                displayEmpty
                label="Value"
                onChange={handleFilterValueDdlChange}
              >
                {filterOperatorValues.map((col, index) => {
                  return (
                    <MenuItem key={`filterMenuItem-${index}`} value={col.key}>
                      {col.value}
                    </MenuItem>
                  );
                })}
              </Select>
            </>
          }
        </FormControl>
      </Grid>




    </Grid>
  );
}
export default FilterFieldComponent;